import React, { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { USERS_LIST_URL, USER_DELETE_URL } from "../../utils/Constant";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import BiddingStatusModal from "./BiddingStatusModal";
import "./UsersListPage.css";

const UsersListPage = () => {
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const token = useSelector((state) => state.token?.token);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(USERS_LIST_URL, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 10000,
      });
      if (!Array.isArray(response.data)) throw new Error("Invalid Users response");
      setUserData(response.data);
    } catch (failure) {
      setError(failure.response?.data?.error || "Could not load users. Please refresh and try again.");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (token) fetchUsers();
    else navigate("/login");
  }, [token, navigate, fetchUsers]);

  function updateUser(user, message) {
    setUserData((users) => users.map((current) => current._id === user._id ? { ...current, ...user } : current));
    if (message) setNotice(message);
  }

  async function confirmDelete() {
    if (!deleteUser || deleting) return;
    setDeleting(true);
    setError("");
    try {
      await axios.delete(`${USER_DELETE_URL}/${deleteUser._id}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
      setUserData((users) => users.filter((user) => user._id !== deleteUser._id));
      setDeleteUser(null);
      setNotice("User deleted.");
    } catch (failure) {
      setError(failure.response?.data?.error || "Deletion could not be confirmed. Refresh the Users page before trying again.");
      setDeleteUser(null);
    } finally { setDeleting(false); }
  }

  const query = search.trim().toLowerCase();
  const visibleUsers = userData.filter((user) => !query ||
    [user.username, user.userId, user.email, user.phone, user.firstName, user.lastName, user._id]
      .some((value) => String(value ?? "").toLowerCase().includes(query)));

  return (
    <DashboardLayout>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Users</h3>
          <div className="card-tools"><a className="btn btn-primary btn-sm" href="/users/create">Add new user</a></div>
        </div>
        <div className="bidding-users-tools">
          <div className="bidding-users-search">
            <label htmlFor="bidder-search">Find a bidder</label>
            <input id="bidder-search" type="search" className="form-control" value={search}
              placeholder="Username, bidder number, email or phone"
              onChange={(event) => setSearch(event.target.value)} />
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={fetchUsers} disabled={loading}>Refresh users</button>
        </div>
        <div className="px-3">
          <p className="small text-muted">Pause stops new bids. Existing bids and payment access remain available.</p>
          {notice && <div className="alert alert-success" role="status">{notice}</div>}
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
        </div>
        <div className="card-body table-responsive p-0">
          <table className="table table-striped projects bidding-users-table">
            <thead><tr><th scope="col">Bidder &amp; bidding access</th><th scope="col">Role</th><th scope="col">Email</th><th scope="col">Phone</th><th scope="col">Account</th><th scope="col">Actions</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center" role="status">Loading users…</td></tr>}
              {!loading && visibleUsers.length === 0 && <tr><td colSpan={6}>No matching users.</td></tr>}
              {!loading && visibleUsers.map((user) => (
                <tr key={user._id}>
                  <td className="bidding-user-identity">
                    <strong>{user.username}</strong><div className="small text-muted">Bidder #{user.userId || user._id}</div>
                    {user.role === "customer" && <div className="bidding-user-access">
                      <span className={`badge ${user.isBiddingPaused ? "badge-warning" : "badge-success"}`}>{user.isBiddingPaused ? "Bidding paused" : "Not paused"}</span>
                      <button type="button" className={`btn btn-sm ${user.isBiddingPaused ? "btn-outline-success" : "btn-warning"}`}
                        onClick={() => { setNotice(""); setSelectedUser(user); }}
                        aria-label={`${user.isBiddingPaused ? "Resume" : "Pause"} bidding for ${user.username}`}>
                        {user.isBiddingPaused ? "Resume bidding" : "Pause bidding"}
                      </button>
                    </div>}
                  </td>
                  <td>{user.role}</td><td>{user.email}</td><td>{user.phone}</td>
                  <td>{user.isActive === false ? "Inactive" : "Active"}</td>
                  <td className="bidding-user-actions">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/user-details/update/${user._id}`)} aria-label={`View ${user.username}`}>View</button>
                    {user.role === "customer" && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setDeleteUser(user)} aria-label={`Delete ${user.username}`}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {selectedUser && <BiddingStatusModal key={selectedUser._id} user={selectedUser} token={token}
        onClose={() => setSelectedUser(null)} onUpdated={updateUser} />}
      <Modal className="bidding-users-modal" show={Boolean(deleteUser)} onHide={deleting ? undefined : () => setDeleteUser(null)}
        backdrop={deleting ? "static" : true} keyboard={!deleting} centered aria-labelledby="delete-user-title">
        <Modal.Header closeButton={!deleting}><Modal.Title id="delete-user-title">Delete user</Modal.Title></Modal.Header>
        <Modal.Body>Delete <strong>{deleteUser?.username}</strong>? Use Pause bidding to stop new bids while keeping the account.</Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setDeleteUser(null)} disabled={deleting}>Cancel</button>
          <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>{deleting ? "Deleting…" : "Confirm delete"}</button>
        </Modal.Footer>
      </Modal>
    </DashboardLayout>
  );
};

export default UsersListPage;
